import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getApplicationRoleType,
  getCurrentUserId,
  getDealId,
  getEmailAddress,
  getEncompassLoanId,
  getLoanId,
  getLoanNumber,
  getLoanOfficerId,
  getLoanPurpose,
  getNhDealApplicationId,
  getProspectId,
  getUserRole,
  setEmailAddress,
} from "features/authSlice";
import { useGetBorrowerDetailsQuery } from "pages/dashboard/home/dashboardService";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";
import { RegistrationRoleType } from "pages/registration/registrationService";
import { DASHBOARD_SESSION_ID } from "utilities/Constant";
import { UqualFullStoryProps } from "utilities/full-story";

export const resolveUqualTrackingEmail = (reduxEmail?: string, profileEmail?: string): string =>
  reduxEmail?.trim() || profileEmail?.trim() || "";

export const mapUqualBorrowerType = (userRole?: string, applicationRoleType?: string): string => {
  const role = userRole?.toLowerCase() ?? "";
  const appRole = applicationRoleType?.toLowerCase() ?? "";

  if (appRole === "primaryborrower") {
    return role === "borrower" ? "Borrower" : "Coborrower";
  }
  if (appRole === "nonspousalcoborrower") {
    return role === "borrower" ? "NSCB" : "NSCB Spouse";
  }

  if (role === RegistrationRoleType.Borrower.toLowerCase()) return "Borrower";
  if (role === RegistrationRoleType.SpousalCoBorrower.toLowerCase()) return "Coborrower";
  if (role === RegistrationRoleType.NonSpousalCoBorrower.toLowerCase()) return "NSCB";

  return "Unknown";
};

export const useUqualFullStoryTrackingProps = (): UqualFullStoryProps => {
  const dispatch = useDispatch();
  const userId = useSelector(getCurrentUserId);
  const emailFromStore = useSelector(getEmailAddress);
  const dealId = useSelector(getDealId);
  const prospectId = useSelector(getProspectId);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);
  const loanNumber = useSelector(getLoanNumber);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanPurpose = useSelector(getLoanPurpose);
  const loanProgress = useSelector(getLoanProgress);
  const loanOfficerId = useSelector(getLoanOfficerId);
  const userRole = useSelector(getUserRole);
  const applicationRoleType = useSelector(getApplicationRoleType);

  const { data: borrowerDetails } = useGetBorrowerDetailsQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: false,
  });

  const email = resolveUqualTrackingEmail(emailFromStore, borrowerDetails?.emailAddress);

  useEffect(() => {
    if (!borrowerDetails?.emailAddress || emailFromStore === borrowerDetails.emailAddress) {
      return;
    }
    dispatch(setEmailAddress(borrowerDetails.emailAddress));
  }, [borrowerDetails?.emailAddress, emailFromStore, dispatch]);

  return useMemo(
    () => ({
      email,
      sessionId: sessionStorage.getItem(DASHBOARD_SESSION_ID),
      dealId: dealId ?? null,
      prospectId: prospectId || null,
      encompassLoanGuid: encompassLoanId || loanId || null,
      encompassLoanNumber: loanNumber || null,
      nhDealApplicationId: nhDealApplicationId || null,
      loanPurpose: loanPurpose || null,
      milestone: loanProgress?.status ?? null,
      wasLoAssigned: Boolean(loanOfficerId),
      borrowerType: mapUqualBorrowerType(userRole, applicationRoleType),
    }),
    [
      email,
      dealId,
      prospectId,
      encompassLoanId,
      loanId,
      loanNumber,
      nhDealApplicationId,
      loanPurpose,
      loanProgress?.status,
      loanOfficerId,
      userRole,
      applicationRoleType,
    ],
  );
};
